import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useTranslation } from 'next-i18next';
import Theme from '../../../styles/Theme';
import {
	LocalStorageAudioStat, LocalStorageAudioState, localStorageGetItem, LocalStorageKeys,
} from '../../../data/LocalStorage';
import { MapStore } from '../../../data/MapStore';
import { useMapBoxDirections } from '../../../data/Mapbox.swr';
import { Capsule } from '../../../models/Capsule';
import { DirectionProfiles } from '../../../models/MapBox';
import { convertSecondsToString } from '../../../utils/time';
import { CapsuleIcon } from '../capsules/CapsuleIcon';
import { IconCheckSVG } from '../svg/IconCheckSVG';
import { IconChevronRight } from '../../icon/IconChevronRight';

import { DistanceIconValueMeter } from './DistanceIconValueMeter';

interface Props {
	capsule: Capsule;
	onClick: (capsule: Capsule) => void;
	directionProfile: DirectionProfiles;
}

const StyledContainer = styled.div`
	width: 100%;
	height: ${(props) => 96 / props.theme.fontSizeBase}rem;
	padding: ${(props) => 16 / props.theme.fontSizeBase}rem ${(props) => 20 / props.theme.fontSizeBase}rem;
	position: relative;

	display: flex;
	flex-direction: row;
	align-items: center;

	border-top: 1px solid ${(props) => props.theme.borderColor};

	cursor: pointer;
`;

const StyledDistance = styled.div`
	position: absolute;

	padding: ${(props) => 4 / props.theme.fontSizeBase}rem ${(props) => 12 / props.theme.fontSizeBase}rem ${(props) => 4 / props.theme.fontSizeBase}rem ${(props) => 8 / props.theme.fontSizeBase}rem;

	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${(props) => 4 / props.theme.fontSizeBase}rem;

	width: 50%;
	height: ${(props) => 24 / props.theme.fontSizeBase}rem;
	top: -${(props) => 12 / props.theme.fontSizeBase}rem;

	margin-left: auto;
	margin-right: auto;
	left:0;
	right:0;
`;

const StyledDistanceFakeContainer = styled.div`
	margin: 0 auto;
	background: ${(props) => props.theme.white};
`;

const StyledTexts = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: ${(props) => 16 / props.theme.fontSizeBase}rem;
	flex: 1;

	min-width: 0;
	min-height: 0;
	overflow: hidden;
`;

const StyledTitle = styled.span`
	font-family: 'Plus Jakarta Sans', sans-serif;
	font-style: normal;
	font-weight: 600;
	font-size: ${(props) => 15 / props.theme.fontSizeBase}rem;
	color: ${(props) => props.theme.black};
	margin-bottom: ${(props) => 4 / props.theme.fontSizeBase}rem;

	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	display:block;
`;

const StyledSubTitle = styled.span`
	font-family: 'Plus Jakarta Sans', sans serif;
	font-style: normal;
	font-weight: 500;
	font-size: ${(props) => 13 / props.theme.fontSizeBase}rem;
	color: ${(props) => props.theme.black};
	opacity: 0.4;

	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	display:block;
`;

const StyledIcon = styled.div`
	width: ${(props) => 48 / props.theme.fontSizeBase}rem;
	height: ${(props) => 48 / props.theme.fontSizeBase}rem;
	border-radius: 50%;
	background-color: ${(props) => props.theme.black};
	display: flex;
    justify-content: center;
	align-items: center;
`;

const StyledChevronRight = styled(IconChevronRight)`
	margin-left: auto;
`;

const StyledState = styled.div`
	margin-top: ${(props) => 10 / props.theme.fontSizeBase}rem;

	display: flex;
	align-items: center;

	span {
		font-family: 'Plus Jakarta Sans', sans-serif;
		font-style: normal;
		font-weight: 400;
		font-size: ${(props) => 12 / props.theme.fontSizeBase}rem;
		line-height: ${(props) => 16 / props.theme.fontSizeBase}rem;
		color: ${(props) => props.theme.black};
		opacity: 0.4;
	}
`;

const StyledStatePlayed = styled.span`
	font-weight: 500;
`;

const StyledStateProgress = styled.div<{ percent: number }>`
	height: ${(props) => 2 / props.theme.fontSizeBase}rem;
	background: #E3E3E3;
	margin-left: ${(props) => 8 / props.theme.fontSizeBase}rem;
	flex: 0.8;

	div {
		width: ${(props) => props.percent}%;
		height: ${(props) => 2 / props.theme.fontSizeBase}rem;

		background: #DE5D3E;
	}
`;

export const ParcoursCapsuleItem = ({
	capsule, onClick, directionProfile,
}: Props) => {
	const { t } = useTranslation(['common']);
	const { userLocation } = MapStore.useState((s) => s);
	const [audioStat, setAudioStat] = useState<LocalStorageAudioStat | null>(null);

	const points = useMemo(() => {
		if (userLocation) {
			return [
				[userLocation.longitude, userLocation.latitude],
				[capsule.attributes.longitude, capsule.attributes.latitude],
			];
		}
		return [];
	}, [capsule.attributes.latitude, capsule.attributes.longitude, userLocation]);

	const { data: mapBoxDirection } = useMapBoxDirections(directionProfile, points);

	useEffect(() => {
		if (capsule && capsule.attributes.audio && capsule.attributes.audio.data) {
			const audioStats = localStorageGetItem<LocalStorageAudioStat[]>(LocalStorageKeys.AUDIO_STATS, '[]');
			const stat = audioStats.find((s) => s.id === capsule.attributes.audio.data?.id);

			setAudioStat(stat || null);
		}
	}, [capsule]);

	return (
		<StyledContainer onClick={() => onClick(capsule)}>
			<StyledDistance>
				<StyledDistanceFakeContainer>
					{ mapBoxDirection && <DistanceIconValueMeter
						directionProfile={directionProfile}
						minutes={mapBoxDirection.duration}
						meters={mapBoxDirection.distance}
					/> }
				</StyledDistanceFakeContainer>
			</StyledDistance>

			<StyledIcon>
				<CapsuleIcon icon={capsule.attributes.icon} size={32} color={Theme.white} />
			</StyledIcon>

			<StyledTexts>
				<StyledTitle>{ capsule.attributes.title }</StyledTitle>
				{ capsule.attributes.subTitle && <StyledSubTitle>
					{ capsule.attributes.subTitle }
				</StyledSubTitle> }
				<StyledState>
					{ audioStat && audioStat.state === LocalStorageAudioState.FINISHED && <>
						<StyledStatePlayed>{ t('common:played') }</StyledStatePlayed>
						<IconCheckSVG width={20} height={20} color={Theme.distanceSelected}/>
					</>}
					{ audioStat
					&& audioStat.state === LocalStorageAudioState.STARTED
					&& capsule.attributes.duration > 0 && <>
						<span>{convertSecondsToString(t, audioStat.remainingTime)} { t('common:remainingsFemale') }</span>
						<StyledStateProgress
							percent={100 - (audioStat.remainingTime / (capsule.attributes.duration * 60)) * 100}>
							<div/>
						</StyledStateProgress>
					</>}
				</StyledState>
			</StyledTexts>

			<StyledChevronRight width={24} height={24} color={Theme.black} />
		</StyledContainer>
	);
};
